/* =============================================================
   АНУ-д суралцах зардлын тооцоолуур
   Visalearn.org/us-cost-calculator-тай ижил функционал
   ============================================================= */
import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, Users, Baby, GraduationCap, BookOpen, School, Info, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const USD_TO_MNT = 3450;

function fmt(usd: number) {
  return `$${usd.toLocaleString()}`;
}
function fmtMnt(usd: number) {
  return `₮${(usd * USD_TO_MNT).toLocaleString()}`;
}

type ProgramType = "community" | "university" | "esl";

interface CostBreakdown {
  visa: number;
  tuition: number;
  flight: number;
  housing: number;
  living: number;
}

const PROGRAMS: Record<ProgramType, { label: string; sub: string; icon: React.ReactNode; costs: CostBreakdown }> = {
  community: {
    label: "Community College",
    sub: "2 жилийн хөтөлбөр — хамгийн хямд хувилбар",
    icon: <School className="w-6 h-6" />,
    costs: {
      visa: 585,      // SEVIS $350 + embassy $235
      tuition: 3500,  // per year average
      flight: 1200,
      housing: 3000,  // 2 months
      living: 2050,   // 2 months
    },
  },
  university: {
    label: "University (Bachelor's)",
    sub: "4 жилийн хөтөлбөр — бакалаврын зэрэг",
    icon: <GraduationCap className="w-6 h-6" />,
    costs: {
      visa: 585,
      tuition: 14000, // per year average
      flight: 1200,
      housing: 4000,  // 2 months
      living: 2500,   // 2 months
    },
  },
  esl: {
    label: "ESL (Language School)",
    sub: "Англи хэлний сургалт — богино хугацаа",
    icon: <BookOpen className="w-6 h-6" />,
    costs: {
      visa: 585,
      tuition: 2000,  // ~3 months
      flight: 1200,
      housing: 2500,  // 2 months
      living: 1800,   // 2 months
    },
  },
};

const COST_LABELS: { key: keyof CostBreakdown; label: string; color: string }[] = [
  { key: "visa",    label: "Визний зардал",              color: "bg-blue-500" },
  { key: "tuition", label: "Сургалтын зардал",           color: "bg-purple-500" },
  { key: "flight",  label: "Нислэгийн зардал",           color: "bg-sky-500" },
  { key: "housing", label: "Байрны зардал (2 сар)",      color: "bg-amber-500" },
  { key: "living",  label: "Амьдралын зардал (2 сар)",   color: "bg-emerald-500" },
];

function Counter({ value, onChange, min = 0, max = 10 }: { value: number; onChange: (v: number) => void; min?: number; max?: number }) {
  return (
    <div className="flex items-center gap-3">
      <button
        onClick={() => onChange(Math.max(min, value - 1))}
        className="w-9 h-9 rounded-full border-2 border-slate-300 flex items-center justify-center text-slate-600 hover:border-blue-500 hover:text-blue-600 transition-colors font-bold text-lg"
      >
        −
      </button>
      <span className="w-8 text-center text-xl font-bold text-slate-800">{value}</span>
      <button
        onClick={() => onChange(Math.min(max, value + 1))}
        className="w-9 h-9 rounded-full border-2 border-slate-300 flex items-center justify-center text-slate-600 hover:border-blue-500 hover:text-blue-600 transition-colors font-bold text-lg"
      >
        +
      </button>
    </div>
  );
}

function CostRow({ label, usd, color }: { label: string; usd: number; color: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className={`w-3 h-3 rounded-full ${color}`} />
          <span className="text-slate-700 font-medium">{label}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="font-bold text-slate-800">{fmt(usd)}</div>
            <div className="text-xs text-slate-500">{fmtMnt(usd)}</div>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 text-sm text-slate-500 border-t border-slate-100 pt-3">
              {label === "Визний зардал" && "SEVIS хураамж $350 + Элчин сайдын яамны хураамж $235 орно."}
              {label === "Сургалтын зардал" && "Сургуулиас хамааран өөрчлөгдөнө. Энэ нь жилийн дундаж үнэ юм."}
              {label === "Нислэгийн зардал" && "Улаанбаатар–АНУ чиглэлийн дундаж нислэгийн үнэ."}
              {label === "Байрны зардал (2 сар)" && "Дотуур байр эсвэл хуваалцан байрлах тохиолдлын дундаж үнэ."}
              {label === "Амьдралын зардал (2 сар)" && "Хоол, тээвэр, утас, бусад өдөр тутмын зардал."}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function USCostCalculator() {
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [program, setProgram] = useState<ProgramType>("community");

  const prog = PROGRAMS[program];

  const totals = useMemo(() => {
    const c = prog.costs;
    // Adults pay full cost; children pay visa + flight + housing + living (no tuition for under 18 in this context)
    const adultTotal = adults * (c.visa + c.tuition + c.flight + c.housing + c.living);
    const childTotal = children * (c.visa + c.flight + c.housing + c.living);
    return {
      visa:    (adults + children) * c.visa,
      tuition: adults * c.tuition,
      flight:  (adults + children) * c.flight,
      housing: (adults + children) * c.housing,
      living:  (adults + children) * c.living,
      grand:   adultTotal + childTotal,
    };
  }, [adults, children, program, prog.costs]);

  const personLabel = `${adults} насанд хүрэгч${children > 0 ? ` · ${children} хүүхэд` : ""} · ${prog.label} · 2 сарын амьдралын зардал орсон`;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0A1628] to-[#1E3A5F] text-white py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400/30 rounded-full px-4 py-1.5 text-sm text-blue-300 mb-6">
            <GraduationCap className="w-4 h-4" />
            АНУ-Д СУРАЛЦАХ ЗАРДЛЫН ТООЦООЛУУР
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">АНУ-д суралцах нийт зардал</h1>
          <p className="text-slate-300 text-lg">
            Гэр бүлийн бүрэлдэхүүн, сургалтын төрлөө сонгоод нийт зардлаа тооцоолоорой.
            Бүх тоо <strong className="text-white">ойролцоо дундаж</strong> үнэ дээр үндэслэсэн.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 items-start">

          {/* Left — Inputs */}
          <div className="space-y-6">

            {/* People */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <h2 className="font-bold text-slate-800 text-lg mb-5 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Хүний тоо
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-700">Насанд хүрэгч</div>
                    <div className="text-sm text-slate-500">18+ нас</div>
                  </div>
                  <Counter value={adults} onChange={setAdults} min={1} />
                </div>
                <div className="border-t border-slate-100" />
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-700 flex items-center gap-2">
                      <Baby className="w-4 h-4 text-slate-400" />
                      Хүүхэд
                    </div>
                    <div className="text-sm text-slate-500">18-аас доош</div>
                  </div>
                  <Counter value={children} onChange={setChildren} min={0} />
                </div>
              </div>
            </div>

            {/* Program type */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <h2 className="font-bold text-slate-800 text-lg mb-5 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-blue-600" />
                Сургалтын төрөл
              </h2>
              <div className="space-y-3">
                {(Object.entries(PROGRAMS) as [ProgramType, typeof PROGRAMS[ProgramType]][]).map(([key, p]) => (
                  <button
                    key={key}
                    onClick={() => setProgram(key)}
                    className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                      program === key
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <span className={`mt-0.5 ${program === key ? "text-blue-600" : "text-slate-400"}`}>
                      {p.icon}
                    </span>
                    <div>
                      <div className={`font-semibold ${program === key ? "text-blue-700" : "text-slate-700"}`}>
                        {p.label}
                      </div>
                      <div className="text-sm text-slate-500 mt-0.5">{p.sub}</div>
                    </div>
                    {program === key && (
                      <div className="ml-auto w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 rounded-full bg-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Disclaimer */}
            <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Энэхүү тооцоо нь <strong>ойролцоо дундаж</strong> үнэ дээр үндэслэсэн бөгөөд хот, сургуулиас хамааран өөрчлөгдөж болно.
                Долларын ханш: <strong>1 USD = ₮{USD_TO_MNT.toLocaleString()}</strong>
              </span>
            </div>
          </div>

          {/* Right — Results */}
          <div className="space-y-4">
            {/* Total */}
            <motion.div
              key={`${adults}-${children}-${program}`}
              initial={{ opacity: 0.7, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-[#1E3A5F] to-[#0A1628] text-white rounded-2xl p-6 shadow-lg"
            >
              <div className="text-sm text-blue-300 uppercase tracking-wider mb-2">Нийт зардал (ойролцоо)</div>
              <div className="text-5xl font-bold mb-1">{fmt(totals.grand)}</div>
              <div className="text-blue-300 text-lg mb-3">≈ {fmtMnt(totals.grand)}</div>
              <div className="text-sm text-slate-400">{personLabel}</div>
            </motion.div>

            {/* Breakdown */}
            <div className="space-y-2">
              {COST_LABELS.map(({ key, label, color }) => (
                <CostRow key={key} label={label} usd={totals[key]} color={color} />
              ))}
            </div>

            {/* Summary table */}
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
              <div className="text-sm font-semibold text-slate-600 mb-3">Нийт дүн</div>
              <div className="space-y-2">
                {COST_LABELS.map(({ key, label, color }) => (
                  <div key={key} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${color}`} />
                      <span className="text-slate-600">{label}</span>
                    </div>
                    <span className="font-semibold text-slate-800">{fmt(totals[key])}</span>
                  </div>
                ))}
                <div className="border-t border-slate-300 pt-2 mt-2 flex items-center justify-between">
                  <span className="font-bold text-slate-800">НИЙТ</span>
                  <div className="text-right">
                    <div className="font-bold text-slate-900 text-lg">{fmt(totals.grand)}</div>
                    <div className="text-xs text-slate-500">{fmtMnt(totals.grand)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center">
              <h3 className="font-bold text-slate-800 mb-2">Виз авах процессыг мэдэх үү?</h3>
              <p className="text-sm text-slate-600 mb-4">
                АНУ-ын оюутны виз авах бүрэн заавар, баримт бичгийн жагсаалт, ярилцлагын бэлтгэл — манай зөвлөхүүд танд туслана.
              </p>
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("open-order-modal"));
                }}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
              >
                Виз мэдүүлэх
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
